export type LogoSourceKind =
  | 'manifest_icon'
  | 'apple_touch_icon'
  | 'rel_icon'
  | 'og_image'
  | 'twitter_image'
  | 'header_image'
  | 'footer_image'
  | 'google_s2_favicon'
  | 'root_favicon'
  | 'clearbit_logo';

const END_USER_SOURCE_LABELS: Record<LogoSourceKind, string> = {
  manifest_icon: 'website icon',
  apple_touch_icon: 'website icon',
  rel_icon: 'website icon',
  og_image: 'social preview image',
  twitter_image: 'social preview image',
  header_image: 'website header',
  footer_image: 'website header',
  google_s2_favicon: 'favicon',
  root_favicon: 'favicon',
  clearbit_logo: 'brand provider',
};

export interface AutoDiscoveredLogoProvenance {
  normalizedSourceDomain?: string | null;
  sourceKind?: string | null;
}

export interface DiscoveredLogoCandidate extends AutoDiscoveredLogoProvenance {
  url: string;
  retrievedAt?: string | null;
  sourceUrl?: string | null;
  /** Deterministic 0–100 candidate score; this is not a probability. */
  qualityScore?: number | null;
  width?: number | null;
  height?: number | null;
  isVector?: boolean | null;
  verificationOutcome?: string | null;
  /** The decision authority persisted by the canonical logo writer. */
  selectedBy?: 'automation' | 'human' | null;
  selectionReason?: string | null;
  /** ENT-6428: discovery's own render admission verdict, passed through unchanged. */
  renderAdmission?: LogoRenderAdmission | null;
}

export interface LogoRenderAdmission {
  outcome: 'renderable' | 'rejected' | 'unknown';
  /** Machine-readable refusal code (for example `below_quality_floor`); null when renderable. */
  reason: string | null;
}

export function getEndUserLogoSourceLabel(sourceKind?: string | null): string | null {
  if (!sourceKind) return null;
  return END_USER_SOURCE_LABELS[sourceKind as LogoSourceKind] ?? null;
}

export function buildAutoDiscoveredLogoProvenanceText({
  normalizedSourceDomain,
  sourceKind,
}: AutoDiscoveredLogoProvenance): string {
  const domain = normalizedSourceDomain?.trim();
  const sourceLabel = getEndUserLogoSourceLabel(sourceKind);
  const fromText = domain ? ` from ${domain}` : '';
  const sourceText = sourceLabel ? ` ${sourceLabel}` : '';

  return `Logo auto-discovered${fromText}${sourceText}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function cleanString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function cleanQualityScore(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100
    ? value
    : null;
}

function cleanDimension(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

function inferSourceKindFromUrl(url: string): LogoSourceKind | null {
  if (/\/\/www\.google\.com\/s2\/favicons/i.test(url)) return 'google_s2_favicon';
  if (/\/\/logo\.clearbit\.com\//i.test(url)) return 'clearbit_logo';
  if (/\/favicon\.(ico|png|svg|webp)(\?|$)/i.test(url)) return 'root_favicon';
  return null;
}

function candidateFromRecord(
  value: unknown,
  fallbackDomain?: string | null,
): DiscoveredLogoCandidate | null {
  if (!isRecord(value)) return null;

  const url = cleanString(value.url) || cleanString(value.logo_url) || cleanString(value.logoUrl);
  if (!url) return null;

  const sourceKind =
    cleanString(value.source_kind) ||
    cleanString(value.sourceKind) ||
    cleanString(value.logo_source_kind) ||
    cleanString(value.logoSourceKind) ||
    inferSourceKindFromUrl(url);

  return {
    url,
    sourceKind,
    retrievedAt:
      cleanString(value.retrieved_at) ||
      cleanString(value.retrievedAt) ||
      cleanString(value.logo_retrieved_at) ||
      cleanString(value.logoRetrievedAt),
    sourceUrl:
      cleanString(value.source_url) ||
      cleanString(value.sourceUrl) ||
      cleanString(value.logo_source_url) ||
      cleanString(value.logoSourceUrl) ||
      url,
    normalizedSourceDomain:
      cleanString(value.normalized_source_domain) ||
      cleanString(value.normalizedSourceDomain) ||
      cleanString(value.source_domain) ||
      cleanString(value.sourceDomain) ||
      fallbackDomain ||
      null,
    qualityScore: cleanQualityScore(value.quality_score) ?? cleanQualityScore(value.qualityScore),
    width: cleanDimension(value.width),
    height: cleanDimension(value.height),
    selectionReason: cleanString(value.selection_reason) || cleanString(value.selectionReason),
    isVector: typeof value.is_vector === 'boolean' ? value.is_vector
      : typeof value.isVector === 'boolean' ? value.isVector : null,
    verificationOutcome: isRecord(value.verification)
      ? cleanString(value.verification.outcome)
      : null,
    selectedBy: (() => {
      const selectedBy = cleanString(value.selected_by) || cleanString(value.selectedBy);
      return selectedBy === 'automation' || selectedBy === 'human' ? selectedBy : null;
    })(),
    renderAdmission: renderAdmissionFromRecord(value.render_admission),
  };
}

function renderAdmissionFromRecord(value: unknown): LogoRenderAdmission | null {
  if (!isRecord(value)) return null;
  const outcome = value.outcome;
  if (outcome !== 'renderable' && outcome !== 'rejected' && outcome !== 'unknown') return null;
  return { outcome, reason: cleanString(value.reason) };
}

export function extractDiscoveredLogoCandidate(
  discoveryData: unknown,
  fallbackDomain?: string | null,
): DiscoveredLogoCandidate | null {
  if (!isRecord(discoveryData)) return null;

  const selectedLogo =
    candidateFromRecord(discoveryData.selected_logo, fallbackDomain) ||
    candidateFromRecord(discoveryData.selectedLogo, fallbackDomain) ||
    candidateFromRecord(discoveryData.logo, fallbackDomain);
  if (selectedLogo) return selectedLogo;

  const branding = discoveryData.branding;
  if (!isRecord(branding)) return null;

  const selectedBrandingLogo =
    candidateFromRecord(branding.selected_logo, fallbackDomain) ||
    candidateFromRecord(branding.selectedLogo, fallbackDomain);
  if (selectedBrandingLogo) return selectedBrandingLogo;

  const clearbitLogo = cleanString(branding.clearbit_logo) || cleanString(branding.clearbitLogo);
  if (clearbitLogo) {
    return {
      url: clearbitLogo,
      sourceKind: 'clearbit_logo',
      normalizedSourceDomain: fallbackDomain || null,
    };
  }

  const logo = cleanString(branding.logo);
  if (logo) {
    return {
      url: logo,
      sourceKind: inferSourceKindFromUrl(logo) || 'rel_icon',
      normalizedSourceDomain: fallbackDomain || null,
    };
  }

  const images = branding.images;
  if (!isRecord(images)) return null;

  const imageLogo = cleanString(images.logo);
  if (imageLogo) {
    return {
      url: imageLogo,
      sourceKind: inferSourceKindFromUrl(imageLogo) || 'rel_icon',
      normalizedSourceDomain: fallbackDomain || null,
    };
  }

  const favicon = cleanString(images.favicon);
  if (favicon) {
    return {
      url: favicon,
      sourceKind: inferSourceKindFromUrl(favicon) || 'root_favicon',
      normalizedSourceDomain: fallbackDomain || null,
    };
  }

  return null;
}

function pushCandidate(
  candidates: DiscoveredLogoCandidate[],
  seenUrls: Set<string>,
  candidate: DiscoveredLogoCandidate | null,
): void {
  if (!candidate || seenUrls.has(candidate.url)) return;
  seenUrls.add(candidate.url);
  candidates.push(candidate);
}

function candidateFromUrl(
  url: unknown,
  sourceKind: LogoSourceKind,
  fallbackDomain?: string | null,
): DiscoveredLogoCandidate | null {
  const cleanUrl = cleanString(url);
  if (!cleanUrl) return null;

  return {
    url: cleanUrl,
    sourceKind,
    normalizedSourceDomain: fallbackDomain || null,
    sourceUrl: cleanUrl,
  };
}

export function extractDiscoveredLogoCandidates(
  discoveryData: unknown,
  fallbackDomain?: string | null,
): DiscoveredLogoCandidate[] {
  const candidates: DiscoveredLogoCandidate[] = [];
  const seenUrls = new Set<string>();
  if (!isRecord(discoveryData)) return candidates;

  pushCandidate(candidates, seenUrls, candidateFromRecord(discoveryData.selected_logo, fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromRecord(discoveryData.selectedLogo, fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromRecord(discoveryData.logo, fallbackDomain));

  const branding = discoveryData.branding;
  if (!isRecord(branding)) return candidates;

  pushCandidate(candidates, seenUrls, candidateFromRecord(branding.selected_logo, fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromRecord(branding.selectedLogo, fallbackDomain));
  // ENT-2973: discovery retains the viable candidate set for operator review.
  // Keep the selected candidate first for backwards-compatible presentation,
  // then append the remaining choices without exposing raw discovery evidence.
  if (Array.isArray(branding.logo_candidates)) {
    for (const candidate of branding.logo_candidates) {
      pushCandidate(candidates, seenUrls, candidateFromRecord(candidate, fallbackDomain));
    }
  }
  pushCandidate(candidates, seenUrls, candidateFromUrl(branding.logo, 'header_image', fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromUrl(branding.clearbit_logo, 'clearbit_logo', fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromUrl(branding.clearbitLogo, 'clearbit_logo', fallbackDomain));

  const images = branding.images;
  if (!isRecord(images)) return candidates;

  pushCandidate(candidates, seenUrls, candidateFromUrl(images.manifest_icon, 'manifest_icon', fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromUrl(images.manifestIcon, 'manifest_icon', fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromUrl(images.apple_touch_icon, 'apple_touch_icon', fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromUrl(images.appleTouchIcon, 'apple_touch_icon', fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromUrl(images.logo, 'header_image', fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromUrl(images.header_image, 'header_image', fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromUrl(images.headerImage, 'header_image', fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromUrl(images.footer_image, 'footer_image', fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromUrl(images.footerImage, 'footer_image', fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromUrl(images.og_image, 'og_image', fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromUrl(images.ogImage, 'og_image', fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromUrl(images.twitter_image, 'twitter_image', fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromUrl(images.twitterImage, 'twitter_image', fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromUrl(images.favicon, 'rel_icon', fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromUrl(images.root_favicon, 'root_favicon', fallbackDomain));
  pushCandidate(candidates, seenUrls, candidateFromUrl(images.rootFavicon, 'root_favicon', fallbackDomain));

  return candidates;
}
